/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2019
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { SVGIcon } from '@ba/ba-ui-carbon-toolkit';
import handle24 from '@ba/ba-graphics/dist/icons-js/handle_24';
import { observer } from 'mobx-react';
import StringResources from '../nls/StringResources';

import './CrosshairsPopOver.scss';

const RESIZE_OFFSET = 2;
const MOVE_STEP = 5;
const HANDLE_BAR_HEIGHT = 10;
const TOP_PADDING = 8;
const ARROW_KEYS = ['ArrowUp', 'ArrowDown', 'ArrowRight', 'ArrowLeft'];

/*
 * The CrosshairsPopOver is a resizable, movable popover
 * It provides information about matching data points if used with the compare line
 * This component can also be used indepently to display data provided to it.
*/
@observer
class CrosshairsPopOver extends Component {
	constructor(props) {
		super(props);
		this.state = {
			x: 0, y: 0,
			dragging: false,
			resizing: false,
			footerItemsVisible: 2
		};
		this.footerHeight = this.props.footerInfo.footerHeight;
		this.containerNode = this.props.containerNode;

		this.popover = React.createRef();
		this.handle = React.createRef();
		this.crossHairInfo = React.createRef();
	}

	onStart(e) {
		this.element = this.popover.current;
		const box = this.element.getBoundingClientRect();
		this.setState({
			x: e.pageX,
			y: e.pageY,
			relX: e.pageX - box.left,
			relY: e.pageY - box.top
		});
	}

	// Mouse move events
	mouseDown = (e) => {
		this.onMouseDown(e);
	}

	mouseUp = (e) => {
		this.onMouseUp(e);
	}

	mouseMove = (e) => {
		this.onMouseMove(e);
	}

	mouseLeave = (e) => {
		this.onMouseLeave(e);
	}

	onMouseDown(e) {
		if (e.button !== 0) return;
		this.setState({ dragging: true });
		this.onStart(e);
		this.containerNode && this.containerNode.addEventListener('mousemove', this.mouseMove);
		this.containerNode && this.containerNode.addEventListener('mouseup', this.mouseUp);
		this.containerNode && this.containerNode.addEventListener('mouseleave', this.mouseLeave);
		e.preventDefault();
	}

	onMouseUp(e) {
		this.setState({ dragging: false });
		this.containerNode && this.containerNode.removeEventListener('mousemove', this.mouseMove);
		this.containerNode && this.containerNode.removeEventListener('mouseup', this.mouseUp);
		this.containerNode && this.containerNode.removeEventListener('mouseleave', this.mouseLeave);
		e.preventDefault();
	}

	onMouseMove(e) {
		if (this.state.dragging) {
			this.onMove(e);
			e.preventDefault();
		}
	}

	onMouseLeave(e){
		this.onMouseUp(e);
		e.preventDefault();
	}

	onMove(e) {
		const x = e.pageX;
		const y = e.pageY;
		const diffX = x - this.state.x;
		const diffY = y - this.state.y;

		if (x !== this.state.x || y !== this.state.y) {
			this.setState({ x, y });
			const element = this.element;
			if (this._isBound(element, diffX, diffY)) {
				element.style.left = (element.offsetLeft + diffX) + 'px';
				element.style.top = (element.offsetTop + diffY) + 'px';
			}
		}
	}

	// Touch move events
	touchStart = (e) => {
		this.onTouchStart(e);
	}

	touchMove = (e) => {
		this.onTouchMove(e);
	}

	touchEnd = (e) => {
		this.onTouchEnd(e);
	}

	onTouchStart(e) {
		this.setState({ dragging: true });
		this.onStart(e.touches[0]);
		this.containerNode && this.containerNode.addEventListener('touchmove', this.touchMove);
		this.containerNode && this.containerNode.addEventListener('touchend', this.touchEnd);
		e.preventDefault();
	}


	onTouchMove(e) {
		if (this.state.dragging) {
			this.onMove(e.touches[0]);
			e.preventDefault();
			e.stopPropagation();
		}
	}

	onTouchEnd(e) {
		this.setState({ dragging: false });
		this.containerNode && this.containerNode.removeEventListener('touchmove', this.touchMove);
		this.containerNode && this.containerNode.removeEventListener('touchend', this.touchEnd);
		e.preventDefault();
	}

	// Resize mouse events
	resizeStartMouse = (e) => {
		this.onResizeStart(e);
	}

	resizeMoveMouse = (e) => {
		this.onResizeMove(e);
	}

	resizeStopMouse = (e) => {
		this.onResizeStop(e);
	}

	onResizeStart(e) {
		if (e.button !== 0) return;
		this.setState({ resizing: true });
		this.onStart(e);
		this.containerNode && this.containerNode.addEventListener('mousemove', this.resizeMoveMouse);
		this.containerNode && this.containerNode.addEventListener('mouseup', this.resizeStopMouse);
		this.containerNode && this.containerNode.addEventListener('mouseleave', this.resizeStopMouse);
		e.preventDefault();
		e.stopPropagation();
	}

	onResizeMove(e) {
		this.resizeBox(e, false);
	}

	//Resizes the popover to fit into current view when view is resized.
	resizeBox(e, isTouchEvent) {
		const event = isTouchEvent ? e.touches[0] : e;
		if (this.state.resizing) {
			const crossHairInfo = this.crossHairInfo;
			const element = this.element;
			const x = event.pageX;
			const y = event.pageY;
			const diffX = x - this.state.x;
			const diffY = y - this.state.y;
			const horizontalOffset = diffX - RESIZE_OFFSET;
			const verticalOffset = diffY - RESIZE_OFFSET;
			if (x !== this.state.x || y !== this.state.y) {
				this.setState({ x, y });
				if (this._isBoundDuringResize(element, horizontalOffset, verticalOffset)) {
					element.style.width = element.offsetWidth + horizontalOffset + 'px';
					element.style.height = element.offsetHeight + verticalOffset + 'px';
					crossHairInfo.current.style.height = (element.offsetHeight - ((HANDLE_BAR_HEIGHT + TOP_PADDING + this.footerHeight + RESIZE_OFFSET))) + 'px';
					if(this.props.footerInfo.showFooter){
						this._calculateVisibleItemsOnResize();
					}
				}
			}
			e.preventDefault();
		}
	}

	onResizeStop(e) {
		this.setState({ resizing: false });
		this.containerNode && this.containerNode.removeEventListener('mousemove', this.resizeMoveMouse);
		this.containerNode && this.containerNode.removeEventListener('mouseup', this.resizeStopMouse);
		this.containerNode && this.containerNode.removeEventListener('mouseleave', this.resizeStopMouse);
		e.preventDefault();
	}

	// Resize touch events
	resizeStartTouch = (e) => {
		this.onTouchResizeStart(e);
	}

	resizeMoveTouch = (e) => {
		this.onTouchResizeMove(e);
	}

	resizeStopTouch = (e) => {
		this.onTouchResizeStop(e);
	}

	onTouchResizeStart(e) {
		this.setState({ resizing: true });
		this.onStart(e.touches[0]);
		this.containerNode && this.containerNode.addEventListener('touchmove', this.resizeMoveTouch, { passive:false });
		this.containerNode && this.containerNode.addEventListener('touchend', this.resizeStopTouch, { passive:false });
		e.preventDefault();
		e.stopPropagation();
	}

	onTouchResizeMove(e) {
		this.resizeBox(e, true);
	}

	onTouchResizeStop(e) {
		this.setState({ resizing: false });
		this.containerNode && this.containerNode.removeEventListener('touchmove', this.resizeMoveTouch, { passive:false });
		this.containerNode && this.containerNode.removeEventListener('touchend', this.resizeStopTouch, { passive:false });
		e.preventDefault();
	}

	// Move key press events
	onKeySelect = (e) => {
		this.onKeyDown(e);
	}

	onKeyDown(e) {
		const element = this.element ? this.element : this.popover.current;
		const reduceOffset = - (MOVE_STEP + RESIZE_OFFSET);
		const increaseOffset = MOVE_STEP - RESIZE_OFFSET;
		const crossHairInfo = this.crossHairInfo;
		switch (e.key) {
			case 'ArrowUp':
				if (e.shiftKey) {
					if (this._isBoundDuringResize(element, 0, reduceOffset)) {
						element.style.height = (element.offsetHeight + reduceOffset) + 'px';
					}
				} else {
					if (this._isBound(element, 0, -MOVE_STEP)) {
						element.style.top = (element.offsetTop - MOVE_STEP) + 'px';
					}
				}
				break;
			case 'ArrowDown':

				if (e.shiftKey) {
					if (this._isBoundDuringResize(element, 0, reduceOffset)) {
						element.style.height = (element.offsetHeight + increaseOffset) + 'px';
					}
				} else {
					if (this._isBound(element, 0, MOVE_STEP)) {
						element.style.top = (element.offsetTop + MOVE_STEP) + 'px';
					}
				}

				break;
			case 'ArrowRight':
				if (e.shiftKey) {
					if (this._isBoundDuringResize(element, 0, reduceOffset)) {
						element.style.width = (element.offsetWidth + increaseOffset) + 'px';
					}
				} else {
					if (this._isBound(element, MOVE_STEP, 0)) {
						element.style.left = (element.offsetLeft + MOVE_STEP) + 'px';
					}
				}
				break;
			case 'ArrowLeft':
				if (e.shiftKey) {
					if (this._isBoundDuringResize(element, 0, reduceOffset)) {
						element.style.width = (element.offsetWidth + reduceOffset) + 'px';
					}
				} else {
					if (this._isBound(element, -MOVE_STEP, 0)) {
						element.style.left = (element.offsetLeft - MOVE_STEP) + 'px';
					}
				}
				break;
		}
		if(ARROW_KEYS.includes(e.key) && e.shiftKey){
			crossHairInfo.current.style.height = (element.offsetHeight - ((HANDLE_BAR_HEIGHT + TOP_PADDING + this.footerHeight + RESIZE_OFFSET))) + 'px';
			this.props.footerInfo.showFooter && this._calculateVisibleItemsOnResize();
		}
		e.preventDefault();
		e.stopPropagation();
	}

	_parentResize() {
		const element = this.element ? this.element : this.popover.current;
		const parentWidth = this.containerNode ? this.containerNode.offsetWidth : 0;
		const parentHeight = this.containerNode ? this.containerNode.offsetHeight : 0;
		const elementRight = element.offsetLeft + element.offsetWidth;
		const elementBottom = element.offsetTop + element.offsetHeight;
		if (elementRight > parentWidth) {
			element.style.left = Math.max((parentWidth - element.offsetWidth), 0) + 'px';
		}
		if (elementBottom > parentHeight) {
			element.style.top = Math.max((parentHeight - element.offsetHeight), 0) + 'px';
		}
	}

	// Bound Validators
	_isBound(element, diffX, diffY) {
		const dimensions = this._getDimensions(element, diffX, diffY);
		const isHorizontallyBound = (dimensions.elementLeft >= 0 && (dimensions.elementRight - RESIZE_OFFSET) <= dimensions.parentWidth);
		const isVerticallyBound = (dimensions.elementTop >= 0 && (dimensions.elementBottom - RESIZE_OFFSET) <= dimensions.parentHeight);
		return isHorizontallyBound && isVerticallyBound;
	}


	_isBoundDuringResize(element, diffX, diffY) {
		const dimensions = this._getDimensions(element, diffX, diffY);
		const isHorizontallyBound = (dimensions.elementRight - RESIZE_OFFSET) <= dimensions.parentWidth;
		const isVerticallyBound = (dimensions.elementBottom - RESIZE_OFFSET) <= dimensions.parentHeight;
		return isHorizontallyBound && isVerticallyBound;
	}

	_getDimensions(element, diffX, diffY) {
		const parentWidth = this.containerNode && this.containerNode.offsetWidth;
		const parentHeight = this.containerNode && this.containerNode.offsetHeight;

		const elementLeft = element.offsetLeft + diffX;
		const elementRight = elementLeft + element.offsetWidth;

		const elementTop = element.offsetTop + diffY;
		const elementBottom = elementTop + element.offsetHeight;
		return {
			parentWidth: parentWidth,
			parentHeight: parentHeight,
			elementLeft: elementLeft,
			elementRight: elementRight,
			elementTop: elementTop,
			elementBottom: elementBottom
		};
	}

	_calculateVisibleItemsOnResize() {
		const popoverHeight = this.popover && this.popover.current.offsetHeight;
		const onResizeVisibleItems = Math.round(Math.abs((popoverHeight - (HANDLE_BAR_HEIGHT + TOP_PADDING + this.footerHeight))) / this.footerHeight);
		this.setState({ footerItemsVisible: onResizeVisibleItems });
	}

	render() {
		this.popover && this.popover.current && this._parentResize();
		return (
			<div draggable="true" tabIndex='0' className='vis_crosshair_popover resizabe' autoFocus ref={this.popover}
				onMouseDown={this.mouseDown} onTouchStart={this.touchStart} onKeyDown={this.onKeySelect}>
				<div className='dragHandle' tabIndex='0' ref={this.handle}>
					<SVGIcon className="dragIcon" iconId={handle24.id} />
				</div>
				<div className='crossHairInfo' ref={this.crossHairInfo} onTouchMove={(e) => e.stopPropagation()}>
					{this.props.crossHairInfo}
				</div>
				{(this.props.footerInfo.showFooter && (this.props.totalItems > 2) && (this.state.footerItemsVisible <= this.props.totalItems)) &&
					<div className='footerNotes'>
						{StringResources.get('n1_of_n2_items', { n1:this.state.footerItemsVisible, n2: this.props.totalItems } )}
					</div>
				}
				<div className='resize' onMouseDown={this.resizeStartMouse}
					onTouchStart={this.resizeStartTouch}>
				</div>
			</div>
		);
	}
}

CrosshairsPopOver.propTypes = {
	crossHairInfo: PropTypes.object,
	totalItems: PropTypes.number,
	footerInfo: PropTypes.object,
	containerNode: PropTypes.object
};

CrosshairsPopOver.defaultProps = {
	crossHairInfo: <div></div>,
	totalItems: 0,
	footerInfo: {},
	containerNode: <div></div>
};

export default CrosshairsPopOver;


// WEBPACK FOOTER //
// ./src/js/lib/@waca/vis-crosshairs/src/js/popover/CrosshairsPopOver.js